import React, { useMemo, useState } from 'react';
import { message } from 'antd';
import EfImg from 'elem/ui/ef-img';
import styles from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import { getHostApi } from '@/utils';
import { LOGIN_MAP } from '@/constants/forum';

interface SocialLoginLinksProps {
    redirectUrl?: string;
    className?: string;
    showLineLogin?: boolean;
    forceLocale?: string;
    beforeSocialLogin?: (item: SocialLinkItem) => boolean;
}

interface SocialLinkItem {
    name: string;
    imgSrc: string;
    link: string;
    dataLayer?: {
        event: string;
        event_name: string;
        button_name: string;
        __btn_id1?: string;
        __btn_id2?: string;
        __btn_type?: string;
    };
}

const PLATFORM_MAP: Record<string, string> = {
    google4website: 'Google',
    x4website: 'X',
    apple4website: 'APPLE',
    line4register: 'LINE',
};

const NAME_TO_TYPE_MAP: Record<string, string> = {
    google: 'google4website',
    x: 'x4website',
    apple: 'apple4website',
    line: 'line4register',
};

const getPlatformName = (type: string) => {
    return PLATFORM_MAP[type] || type || 'Unknown';
};

const SocialLoginLinks: React.FC<SocialLoginLinksProps> = ({
    redirectUrl = '',
    className = '',
    showLineLogin = true,
    forceLocale,
    beforeSocialLogin,
}) => {
    const { useRouter, Link } = useGlobal();
    const { locale: routerLocale, query } = useRouter();
    const router = useRouter();
    const [loading, setLoading] = useState<string>('');

    const effectiveLocale = forceLocale || routerLocale;

    const WEB_HOST = process.env.NEXT_PUBLIC_WEB_HOST;
    const HOMEBATTERY_REDIRECT = process.env.NEXT_PUBLIC_HOME_BATTERY_DOMAIN;
    const LOGIN_HOST = getHostApi(effectiveLocale).NEXT_PUBLIC_LOGIN_HOST;

    let HOME_HOST = `${WEB_HOST}/${effectiveLocale}`;

    if (redirectUrl.indexOf('https://homebattery') > -1 || redirectUrl.indexOf('https://enterprise') > -1) {
        HOME_HOST = `${HOMEBATTERY_REDIRECT}${effectiveLocale}`;
    }

    const getShopifyUrl = (locale: string) => `https://${locale || 'us'}.ecoflow.com`;

    /**
     * @description: 判断链接是内部链接还是外部，然后统一转换成完整url
     * @params url {string}
     * @return url {string}
     */
    const linkHandle = (url: string) => {
        const regex = /^(http|https|ftp):\/\//;
        if (!regex.test(url)) {
            return `${WEB_HOST}${url}`;
        }
        return url;
    };

    // 获取第三方登录链接
    const getThirdLink = (type: string) => {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        // let url = `${LOGIN_HOST}/auth/oauth/webLogin/${type}?returnTo=${HOME_HOST}&shopifyDomain=${getShopifyUrl(
        //     router.locale,
        // )}&returnToLogout=${`${HOME_HOST}/my-account-manage`}&returnToNew=${linkHandle(redirectUrl || HOME_HOST)}`;
        let url = `${LOGIN_HOST}/auth/oauth/webLoginUrl/${type}?returnTo=${HOME_HOST}&shopifyDomain=${getShopifyUrl(
            effectiveLocale,
        )}&returnToLogout=${`${HOME_HOST}/my-account-manage`}&returnToNew=${linkHandle(
            redirectUrl || HOME_HOST,
        )}&entry_position=none&entry_name=${getPlatformName(type)} registration&page_url=${origin}${pathname}&source_channel=Website`;
        const { inviteCode } = query;
        if (inviteCode) {
            if (!url.includes(inviteCode)) {
                url += `&inviteCode2=${inviteCode}`;
            }
        }
        return url;
    };

    // JP Link Third Login
    const getLinkAuth = (type: string) => {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        let url = `${LOGIN_HOST}/auth/oauth/webLoginUrl/${type}?returnTo=${HOME_HOST}&&returnToNew=${linkHandle(
            redirectUrl || HOME_HOST,
        )}&entry_position=none&entry_name=${getPlatformName(type)} registration&page_url=${origin}${pathname}&source_channel=Website`;
        const { inviteCode } = query;
        if (inviteCode) url += `&inviteCode2=${inviteCode}`;
        return url;
    };

    // 构建URL查询参数
    const buildQueryString = (params: Record<string, string>) => {
        return Object.entries(params)
            .filter(([_, value]) => value !== undefined && value !== null)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');
    };

    // const loginType =
    // item.name === 'google'
    //     ? 'google4website'
    //     : item.name === 'x'
    //     ? 'x4website'
    //     : item.name === 'apple'
    //     ? 'apple4website'
    //     : item.name === 'line'
    //     ? 'line4register'
    //     : item.name;

    // 使用fetch处理第三方登录（不携带cookie）
    const handleThirdPartyLogin = async (type: string, isJpLine = false) => {
        console.log('开始处理第三方登录:', type, isJpLine);
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        // const href = typeof window !== 'undefined' ? window.location.href : '';

        try {
            setLoading(type);

            // 添加邀请码
            const { inviteCode, _source } = query;
            const isForum = _source === LOGIN_MAP.ECOFLOW_WEB_COMMUNITY;
            const params: any = {
                returnTo: HOME_HOST,
                shopifyDomain: getShopifyUrl(effectiveLocale),
                returnToLogout: `${HOME_HOST}/my-account-manage`,
                returnToNew: linkHandle(redirectUrl || HOME_HOST),
                entry_position: 'none',
                entry_name: `${getPlatformName(type)} registration`,
                // page_url: `${href}`,
                page_url: `${origin}${pathname}`,
                source_channel: isForum ? 'Forum' : 'Website',
                authScene: isForum ? 'ECOFLOW_WEB_COMMUNITY' : undefined,
            };
            console.log('params', params);

            // X 需要redirectUrl才能登录成功后但回到原页面，但是其他第三方不能加，否则报错：
            if (type === 'x4website') {
                params.redirectUrl = linkHandle(redirectUrl || HOME_HOST);
            }

            if (inviteCode) {
                params.inviteCode2 = inviteCode;
            }

            // 根据类型选择不同的API端点
            const endpoint = `/auth/oauth/webLoginUrl/${type}`;
            const queryString = buildQueryString(params);
            const fullUrl =
                _source === LOGIN_MAP.ECOFLOW_WEB_COMMUNITY && type === 'google4website'
                    ? `${LOGIN_HOST}/auth/oauth/webLoginUrl/google4community?${queryString}`
                    : `${LOGIN_HOST}${endpoint}?${queryString}`;

            console.log('请求URL:', fullUrl);
            console.log('请求参数:', params);

            // 使用fetch发起GET请求，明确设置不携带cookie
            const response = await fetch(fullUrl, {
                method: 'GET',
                credentials: 'omit', // 不携带任何cookie
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'Cache-Control': 'no-cache',
                },
                // 设置超时
                signal: AbortSignal.timeout(30000), // 30秒超时
            });

            console.log('响应状态:', response.status, response.statusText);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('API响应:', data);

            if (data && data.code === '0') {
                if (forceLocale) {
                    localStorage.setItem('forum_country', forceLocale);
                }
                window.location.href = data.data.redirectUrl;
            } else {
                message.error(data.message);
            }
        } catch (error) {
            message.error('erro');
        } finally {
            setLoading('');
        }
    };

    const shareLinks = useMemo(() => {
        const baseOpt: SocialLinkItem[] = [
            {
                name: 'google',
                imgSrc: 'https://websiteoss.ecoflow.com/upload/image/1751531200160/google.png',
                link: getThirdLink('google4website'),
                dataLayer: {
                    event: 'uaEvent',
                    event_name: 'click_login',
                    button_name: 'Google',

                    __btn_id1: 'google',
                    __btn_id2: 'google',
                    __btn_type: 'common',
                },
            },
            {
                name: 'x',
                imgSrc: 'https://websiteoss.ecoflow.com/upload/image/1751531205554/x.png',
                link: getThirdLink('x4website'),
                dataLayer: {
                    event: 'uaEvent',
                    event_name: 'click_login',
                    button_name: 'x',
                    __btn_id1: 'x',
                    __btn_id2: 'x',
                    __btn_type: 'common',
                },
            },
            {
                name: 'apple',
                imgSrc: 'https://websiteoss.ecoflow.com/upload/image/1751531203039/apple.png',
                link: getThirdLink('apple4website'),
                dataLayer: {
                    event: 'uaEvent',
                    event_name: 'click_login',
                    button_name: 'Apple',
                    __btn_id1: 'apple',
                    __btn_id2: 'apple',
                    __btn_type: 'common',
                },
            },
        ];

        if (effectiveLocale === 'jp' && showLineLogin) {
            baseOpt.push({
                name: 'line',
                imgSrc: 'https://websiteoss.ecoflow.com/upload/image/1752049932435/line.png',
                link: getLinkAuth('line4register'),
                dataLayer: {
                    event: 'uaEvent',
                    event_name: 'click_login',
                    button_name: 'LINE',
                },
            });
        }

        return baseOpt;
    }, [HOME_HOST, LOGIN_HOST, WEB_HOST, effectiveLocale, query, redirectUrl, showLineLogin]);

    // 处理点击事件
    const handleLoginClick = (item: SocialLinkItem, e: React.MouseEvent) => {
        console.log('点击事件触发:', item.name);

        e.preventDefault();
        e.stopPropagation();

        if (beforeSocialLogin && !beforeSocialLogin(item)) {
            return;
        }

        // 防止重复点击
        if (loading) {
            console.log('正在加载中，忽略点击');
            return;
        }

        // 触发数据层事件
        if (item.dataLayer && typeof window !== 'undefined' && (window as any).dataLayer) {
            console.log('触发dataLayer事件:', item.dataLayer);
            (window as any).dataLayer.push(item.dataLayer);
        }

        // 判断是否为日本LINE登录
        const isJpLine = item.name === 'line' && effectiveLocale === 'jp';

        // 获取对应的登录类型
        const loginType = NAME_TO_TYPE_MAP[item.name] || item.name;

        console.log('准备调用handleThirdPartyLogin:', loginType, isJpLine);

        // 使用fetch方式处理登录
        handleThirdPartyLogin(loginType, isJpLine);
    };

    return (
        <div className={`${styles.shareLinksContent} ${className}`}>
            {shareLinks.map((item: SocialLinkItem) => (
                <div className="shareLinkItem" key={item.name}>
                    <div
                        className={'GtmModuleData-btn-common ' + item.name}
                        data-btn-id={item.name}
                        onClick={(e) => handleLoginClick(item, e)}
                        style={{
                            cursor: loading === item.name ? 'not-allowed' : 'pointer',
                            position: 'relative',
                            pointerEvents: 'auto', // 确保可以点击
                            userSelect: 'none', // 防止文本选择
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleLoginClick(item, e as any);
                            }
                        }}
                    >
                        <EfImg pc={item.imgSrc} />
                    </div>
                </div>
            ))}
        </div>
    );
};

export default SocialLoginLinks;
